import React, { useState, useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Star, ChevronDown, Truck, ShieldCheck, Package } from "lucide-react";
import { toast } from "sonner";
import BotanicalBackdrop, { LeafGlyph } from "@/components/BotanicalBackdrop";
import IngredientLibrary from "@/components/IngredientLibrary";
import Man360Story from "@/components/Man360Story";
import { useProducts, useProductBySlug } from "@/context/ProductsContext";
import usePageTitle from "@/hooks/usePageTitle";
import useWhatsAppOrderEnabled from "@/hooks/useWhatsAppOrderEnabled";
import { INGREDIENTS } from "@/data/ingredients";
import { reviewsForProduct } from "@/data/reviews";
import ProductImage from "@/components/ProductImage";
import BotanicalIcon from "@/components/BotanicalIcon";
import PincodeChecker from "@/components/PincodeChecker";
import { useCart } from "@/context/CartContext";
import { useEnquiry } from "@/App";
import { useMetaPixel } from "@/context/MetaPixelContext";
import { useAnalytics } from "@/context/AnalyticsContext";

const Stars = ({ n }) => (
  <div className="flex items-center gap-0.5 text-[var(--hk-orange)]">
    {Array.from({ length: 5 }, (_, i) => (
      <Star key={i} size={13} strokeWidth={1.4} fill={i < n ? "currentColor" : "none"} />
    ))}
  </div>
);

const FaqItem = ({ item, open, onToggle, idx }) => (
  <div className="hk-card overflow-hidden" data-testid={`pdp-faq-${idx}`}>
    <button onClick={onToggle} className="w-full text-left px-5 py-4 flex items-center justify-between gap-4">
      <span className="serif text-[16px] text-[var(--hk-ink)] leading-snug pr-4">{item.q}</span>
      <ChevronDown size={16} className={"text-[var(--hk-orange-deep)] transition-transform shrink-0 " + (open ? "rotate-180" : "")} />
    </button>
    <div className={"grid transition-all duration-500 ease-out " + (open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0")}>
      <div className="overflow-hidden">
        <div className="px-5 pb-4 text-[13.5px] leading-[1.75] text-[var(--hk-ink-2)]">{item.a}</div>
      </div>
    </div>
  </div>
);

const ProductDetailPage = () => {
  const { slug } = useParams();
  const { allProducts, loading } = useProducts();
  const product = useProductBySlug(slug);
  usePageTitle(product ? `${product.name} · Premium Ayurvedic Wellness` : "Product");
  const [galleryIdx, setGalleryIdx] = useState(0);
  const [qty, setQty] = useState(1);
  const [openFaq, setOpenFaq] = useState(0);
  const cart = useCart();
  const whatsappOrderEnabled = useWhatsAppOrderEnabled() === true; // Admin › General Settings "WhatsApp Order Button"
  const enq = useEnquiry();
  const pixel = useMetaPixel();
  const analytics = useAnalytics();

  // Fire ViewContent once product is loaded (deduped per slug within this mount)
  useEffect(() => {
    if (!product) return;
    const payload = {
      content_ids: [product.slug],
      content_name: product.name,
      content_type: "product",
      value: product.price ? (product.price / 100) : undefined,
      currency: "INR",
    };
    if (product.category) payload.content_category = product.category;
    pixel.track("ViewContent", payload);
    analytics.fireProductViewed(product);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.slug]);

  // Wait for the products API to hydrate before deciding this is a 404 —
  // otherwise the first render (empty catalogue) bounces the visitor away.
  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-[var(--hk-ink-2)]">Loading…</div>;
  if (!product) return <Navigate to="/products" replace />;

  // Normalise optional list / object fields so admin-created products
  // (which may not populate every legacy hardcoded field) don't crash on `.map`.
  const safeProduct = {
    gallery: [],
    benefits: [],
    howToUse: [],
    faqs: [],
    ingredients: [],
    whoItsFor: [],
    packDetails: {},
    ...product,
  };
  const productProxy = safeProduct;

  const isAvailable = product.status === "available";
  const isMan360 = product.slug === "man-360";
  const reviews = reviewsForProduct(product.slug);
  const related = allProducts.filter((p) => p.slug !== product.slug).slice(0, 4);

  const handleAddToCart = () => {
    cart.addItem(product.slug, qty);
    toast.success(`${product.name} added to cart`);
  };

  const handleBuyNow = () => {
    cart.addItem(product.slug, qty);
    window.location.href = "/checkout";
  };

  const handleNotify = () => enq && enq.openEnquiry(`${product.name} — Waitlist`);
  const handleWhatsappOrder = () => {
    const message = encodeURIComponent(
      `Hi HerbKranti, I'd like to order ${qty} × ${product.name} (${product.packSize}). Please share pricing and delivery details.`
    );
    window.open(`https://wa.me/917249797972?text=${message}`, "_blank");
  };

  return (
    <>
      {/* Back to Products */}
      <div className="mx-auto max-w-7xl px-6 lg:px-10 pt-8">
        <Link to="/products" data-testid="pdp-back-btn" className="inline-flex items-center gap-2 text-[13px] tracking-widest uppercase text-[var(--hk-ink-2)] hover:text-[var(--hk-orange-deep)] transition-colors">
          <ArrowLeft size={14} /> Back to Products
        </Link>
      </div>

      {/* Hero */}
      <section className="relative pt-6 pb-16 lg:pb-24 overflow-hidden">
        <BotanicalBackdrop variant="corners" />
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-14">
          {/* Gallery */}
          {(() => {
            // Normalise gallery entries: admin now saves {url, alt} objects
            // while legacy hardcoded rows are plain strings.
            const galleryRaw = product.gallery || [];
            const gallery = galleryRaw
              .map((g) => (typeof g === "string" ? { url: g, alt: product.name } : { url: g?.url || "", alt: g?.alt || product.name }))
              .filter((g) => !!g.url);
            const idx = Math.min(galleryIdx, Math.max(0, gallery.length - 1));
            const current = gallery[idx];
            // Cache-bust when the admin updates the record so the CDN / browser
            // fetches the fresh image after a Publish.
            const cacheKey = product.updated_at ? `?v=${encodeURIComponent(product.updated_at)}` : "";
            const withV = (u) => (u ? `${u}${u.includes("?") ? "&" : "?"}${cacheKey.slice(1)}` : u);
            const cols = gallery.length <= 4 ? gallery.length : 4;
            return (
              <div className="lg:col-span-6">
                <div className="hk-card p-4 lg:p-8 bg-white aspect-[4/5] flex items-center justify-center overflow-hidden">
                  {current ? (
                    <img src={cacheKey ? withV(current.url) : current.url} alt={current.alt} className="w-full h-full object-contain" data-testid="pdp-gallery-main" onError={(e) => { e.target.style.display = "none"; }} />
                  ) : (
                    <div className="w-full h-full"><ProductImage product={product} /></div>
                  )}
                </div>
                {gallery.length > 1 && (
                  <div className={"mt-4 grid gap-3 grid-cols-" + cols} style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }} data-testid="pdp-gallery-thumbs">
                    {gallery.map((g, i) => (
                      <button
                        key={i}
                        onClick={() => setGalleryIdx(i)}
                        className={"aspect-square rounded-[10px] overflow-hidden border-2 transition-colors " + (idx === i ? "border-[var(--hk-orange)]" : "border-transparent hover:border-[var(--hk-border)]")}
                        data-testid={`pdp-gallery-thumb-${i}`}
                      >
                        <img src={cacheKey ? withV(g.url) : g.url} alt={g.alt} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Product info */}
          <div className="lg:col-span-6 hk-fade-up">
            <div className="label-eyebrow flex items-center gap-2">
              <LeafGlyph className="w-3 h-3 text-[var(--hk-gold-deep)]" />
              {product.statusLabel}
              <span className="opacity-60">· {product.packSize}</span>
            </div>
            <h1 className="serif mt-3 text-[36px] sm:text-[44px] lg:text-[52px] leading-[1.05] text-[var(--hk-ink)] tracking-tight" data-testid="pdp-title">
              {product.name}
            </h1>
            <p className="mt-3 italic-accent text-[var(--hk-orange-deep)] text-[18px] sm:text-[20px]">{product.tagline}</p>

            {reviews.length > 0 && (
              <div className="mt-5 flex items-center gap-3">
                <Stars n={5} />
                <span className="text-[13px] text-[var(--hk-ink-2)]">
                  <span className="font-medium text-[var(--hk-ink)]">{reviews.length}</span> customer {reviews.length === 1 ? "review" : "reviews"}
                </span>
              </div>
            )}

            <p className="mt-6 text-[15px] leading-[1.85] text-[var(--hk-ink-2)]">{product.overview}</p>

            <div className="mt-8 hk-card p-6 md:p-7 bg-white">
              {isAvailable ? (
                <>
                  <div className="flex flex-wrap items-baseline gap-3">
                    <span className="serif text-[36px] text-[var(--hk-ink)]" data-testid="pdp-price">{product.priceDisplay}</span>
                    {product.offer && (
                      <span className="px-3 py-1 rounded-full bg-[var(--hk-orange)] text-white text-[11px] tracking-[0.2em] uppercase" data-testid="pdp-offer">
                        {product.offer}
                      </span>
                    )}
                  </div>
                  {product.offerDescription && <p className="mt-2 text-[13px] text-[var(--hk-ink-2)]">{product.offerDescription}</p>}
                  <div className="mt-6 flex items-center gap-4">
                    <div className="inline-flex items-center border border-[var(--hk-border)] rounded-full bg-white" data-testid="pdp-qty-selector">
                      <button onClick={() => setQty(Math.max(1, qty - 1))} data-testid="pdp-qty-decr" className="w-10 h-10 flex items-center justify-center text-[var(--hk-ink-2)] hover:text-[var(--hk-orange-deep)]">−</button>
                      <span className="w-10 text-center text-[15px] tabular-nums" data-testid="pdp-qty-value">{qty}</span>
                      <button onClick={() => setQty(qty + 1)} data-testid="pdp-qty-incr" className="w-10 h-10 flex items-center justify-center text-[var(--hk-ink-2)] hover:text-[var(--hk-orange-deep)]">+</button>
                    </div>
                    <span className="text-[12px] text-[var(--hk-ink-3)]">
                      {product.offer ? `Ships ${qty * 2} bottles` : `Ships ${qty} bottle${qty > 1 ? "s" : ""}`}
                    </span>
                  </div>
                  <div className="mt-6 flex flex-wrap gap-3">
                    <button className="hk-btn-primary" onClick={handleBuyNow} data-testid="pdp-buy-now-btn">Buy Now <ArrowRight size={16} /></button>
                    <button className="hk-btn-ghost" onClick={handleAddToCart} data-testid="pdp-add-cart-btn">Add to Cart</button>
                    {whatsappOrderEnabled && (
                      <button className="hk-btn-ghost !text-[#25D366] !border-[#25D366]/40 hover:!bg-[#25D366]/5" onClick={handleWhatsappOrder} data-testid="pdp-whatsapp-order-btn">WhatsApp Order</button>
                    )}
                  </div>
                </>
              ) : (
                <div>
                  <div className="label-eyebrow !text-[var(--hk-orange-deep)]">Coming Soon</div>
                  <h3 className="serif mt-2 text-[24px] text-[var(--hk-ink)]">This product is in development.</h3>
                  <p className="mt-2 text-[13.5px] text-[var(--hk-ink-2)] leading-relaxed">Join the waitlist and we'll notify you on launch day.</p>
                  <div className="mt-5 flex flex-wrap gap-3">
                    <button className="hk-btn-primary" onClick={handleNotify} data-testid="pdp-notify-btn">Notify Me at Launch</button>
                    <a href="https://wa.me/917249797972" target="_blank" rel="noreferrer" className="hk-btn-ghost !text-[#25D366] !border-[#25D366]/40 hover:!bg-[#25D366]/5" data-testid="pdp-whatsapp-btn">WhatsApp us</a>
                  </div>
                </div>
              )}

              <div className="mt-6 pt-6 border-t border-[var(--hk-border)] grid grid-cols-3 gap-3 text-[11px] tracking-widest uppercase text-[var(--hk-ink-3)]">
                <div className="flex items-center gap-2"><Truck size={13} /> Pan-India</div>
                <div className="flex items-center gap-2"><ShieldCheck size={13} /> Provenance</div>
                <div className="flex items-center gap-2"><Package size={13} /> Premium Pack</div>
              </div>

              {/* Delivery / pincode checker */}
              <PincodeChecker subtotal={product.price || 0} className="mt-6" />
            </div>
          </div>
        </div>
      </section>

      {/* MAN 360 flagship story replaces the default Benefits + How-to-Use + Philosophy */}
      {isMan360 && <Man360Story />}

      {/* Product-specific Benefits (all products except MAN 360) */}
      {!isMan360 && (
      <section className="relative py-20 bg-[var(--hk-warm-ivory)] overflow-hidden">
        <BotanicalBackdrop variant="sideRight" />
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="max-w-2xl mb-12">
            <div className="label-eyebrow">Why Choose {product.name}?</div>
            <h2 className="serif mt-3 text-[32px] sm:text-[42px] leading-[1.05] text-[var(--hk-ink)]" data-testid="pdp-benefits-title">
              Considered benefits,
              <br />
              <span className="italic-accent text-[var(--hk-orange-deep)]">honestly stated.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="pdp-benefits-grid">
            {(product.benefits || []).map((b, i) => (
              <div key={i} className="hk-card p-8" data-testid={`pdp-benefit-${i}`}>
                <div className="w-9 h-9 rounded-full bg-[var(--hk-warm-ivory)] border border-[var(--hk-border)] flex items-center justify-center text-[var(--hk-orange-deep)]">
                  <LeafGlyph className="w-4 h-4" />
                </div>
                <h3 className="serif mt-5 text-[20px] leading-snug text-[var(--hk-ink)]">{b.title}</h3>
                <p className="mt-3 text-[13.5px] leading-[1.75] text-[var(--hk-ink-2)]">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* How to Use — default ritual for non-MAN 360 products */}
      {!isMan360 && (
      <section className="relative py-20 lg:py-24 overflow-hidden" data-testid="pdp-ritual-section">
        <BotanicalBackdrop variant="sideLeft" />
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="label-eyebrow">{product.ritualEyebrow || "How to Use"}</div>
            <h2 className="serif mt-3 text-[32px] sm:text-[42px] leading-[1.05] text-[var(--hk-ink)]" data-testid="pdp-ritual-title">
              {product.ritualTitle || "A considered daily ritual."}
            </h2>
            <ol className="mt-10 space-y-6" data-testid="pdp-howtouse">
              {(product.howToUse || []).map((s, i) => (
                <li key={i} className="flex gap-5 items-start hk-fade-up" style={{ animationDelay: `${i * 70}ms` }}>
                  <div className="relative shrink-0">
                    <div className="w-11 h-11 rounded-full bg-[var(--hk-orange)] text-white flex items-center justify-center serif text-[16px] shadow-[0_8px_20px_-6px_rgba(217,117,53,0.5)]">
                      {s.step}
                    </div>
                    {i < (product.howToUse || []).length - 1 && (
                      <div className="absolute left-1/2 -translate-x-1/2 top-11 w-px h-8 bg-gradient-to-b from-[var(--hk-gold)] to-transparent" aria-hidden="true" />
                    )}
                  </div>
                  <div className="flex-1 pt-1">
                    <div className="text-[11px] tracking-[0.24em] uppercase text-[var(--hk-orange-deep)]">
                      Step {i + 1} · {s.label}
                    </div>
                    <p className="mt-2 serif text-[18px] leading-snug text-[var(--hk-ink)]" data-testid={`pdp-howtouse-step-${i + 1}`}>
                      {s.body}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          {product.wellnessNote && (
            <aside className="lg:col-span-5" data-testid="pdp-wellness-note">
              <div className="lg:sticky lg:top-24 relative rounded-[16px] overflow-hidden border border-[var(--hk-gold)]/40 bg-white p-8 md:p-10 shadow-[0_20px_50px_-20px_rgba(30,35,31,0.18)]">
                <div className="absolute -top-8 -right-8 w-40 h-40 opacity-[0.08] text-[var(--hk-orange-deep)]" aria-hidden="true">
                  <BotanicalIcon variant="vine" />
                </div>
                <div className="absolute -bottom-10 -left-10 w-32 h-32 opacity-[0.07] text-[var(--hk-gold-deep)]" aria-hidden="true">
                  <BotanicalIcon variant="tulsi" />
                </div>
                <div className="relative">
                  <LeafGlyph className="text-[var(--hk-gold-deep)] w-6 h-6" />
                  <div className="label-eyebrow mt-4">Wellness Note</div>
                  <h3 className="serif mt-3 text-[24px] sm:text-[28px] leading-tight text-[var(--hk-ink)]">{product.wellnessNote.title}</h3>
                  <p className="mt-5 text-[14px] leading-[1.85] text-[var(--hk-ink-2)]">{product.wellnessNote.body}</p>
                </div>
              </div>
            </aside>
          )}
        </div>
      </section>
      )}

      {/* Ingredients — for MAN 360 show full library, others show list */}
      <section className="relative py-20 overflow-hidden">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          {product.ingredientKey === "man360-13" ? (
            <IngredientLibrary />
          ) : (
            <>
              <div className="max-w-2xl mb-8">
                <div className="label-eyebrow">Ingredients</div>
                <h2 className="serif mt-3 text-[32px] sm:text-[42px] leading-[1.05] text-[var(--hk-ink)]">
                  What's <span className="italic-accent text-[var(--hk-orange-deep)]">actually inside.</span>
                </h2>
              </div>
              <div className="grid md:grid-cols-2 gap-6" data-testid="pdp-ingredients-grid">
                {(product.ingredients || []).map((ing, i) => (
                  <div key={i} className="hk-card p-8">
                    <div className="label-eyebrow">{ing.latin}</div>
                    <h3 className="serif mt-2 text-[24px] leading-tight text-[var(--hk-ink)]">{ing.name}</h3>
                    <p className="mt-3 text-[14px] leading-[1.75] text-[var(--hk-ink-2)]">{ing.note}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Philosophy — hidden for MAN 360 (its own 360 Philosophy section replaces it) */}
      {!isMan360 && (
      <section className="relative py-20 bg-[var(--hk-warm-ivory)]">
        <div className="mx-auto max-w-4xl px-6 lg:px-10 text-center">
          <LeafGlyph className="mx-auto text-[var(--hk-gold-deep)] w-6 h-6" />
          <div className="label-eyebrow mt-3">Our Philosophy</div>
          <h2 className="serif mt-4 text-[28px] sm:text-[36px] leading-tight text-[var(--hk-ink)]" data-testid="pdp-philosophy">
            {product.philosophy}
          </h2>
        </div>
      </section>
      )}

      {/* Pack Details + Storage */}
      <section className="relative py-20" data-testid="pdp-pack-storage-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-2 gap-10">
          <div>
            <div className="label-eyebrow">Pack Details</div>
            <h3 className="serif mt-3 text-[28px] leading-snug text-[var(--hk-ink)]">What's in the pack.</h3>
            <ul className="mt-6 space-y-4 text-[14px] text-[var(--hk-ink-2)]" data-testid="pdp-pack-details">
              {Object.entries(product.packDetails || {}).map(([k, v]) => (
                <li key={k} className="flex flex-col gap-1 border-b border-[var(--hk-border)] pb-3">
                  <span className="label-eyebrow !text-[10px]">
                    {k.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase()).trim()}
                  </span>
                  <span className="leading-[1.65]">{v}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="label-eyebrow">Storage</div>
            <h3 className="serif mt-3 text-[28px] leading-snug text-[var(--hk-ink)]">Keep it right.</h3>
            <p className="mt-6 text-[14.5px] leading-[1.85] text-[var(--hk-ink-2)] whitespace-pre-line" data-testid="pdp-storage">
              {product.storage}
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative py-20 bg-[var(--hk-warm-ivory)]">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <div className="mb-8">
            <div className="label-eyebrow">Questions</div>
            <h2 className="serif mt-2 text-[28px] sm:text-[36px] text-[var(--hk-ink)] leading-tight">Everything you might ask.</h2>
          </div>
          <div className="space-y-3" data-testid="pdp-faqs">
            {(product.faqs || []).map((f, i) => (
              <FaqItem key={i} idx={i} item={f} open={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? -1 : i)} />
            ))}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="relative py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-6">
            <div>
              <div className="label-eyebrow">Rituals in Practice</div>
              <h2 className="serif mt-2 text-[28px] sm:text-[36px] text-[var(--hk-ink)] leading-tight">
                Reviews of {product.name}.
              </h2>
            </div>
            <Link to="/reviews" className="hk-btn-ghost">All Reviews</Link>
          </div>
          {reviews.length === 0 ? (
            <div className="hk-card p-12 text-center" data-testid="pdp-reviews-empty">
              <LeafGlyph className="mx-auto text-[var(--hk-gold-deep)] w-6 h-6" />
              <div className="serif mt-4 text-[20px] text-[var(--hk-ink)]">Customer experiences will be added after verified purchases.</div>
              <p className="mt-2 text-[13px] text-[var(--hk-ink-2)]">We only publish reviews from real, verified customers. Reviews for this product will appear here after launch.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="pdp-reviews">
              {reviews.map((r) => (
                <figure key={r.id} className="hk-card p-8">
                  <Stars n={r.rating} />
                  <blockquote className="serif mt-4 text-[17px] leading-snug text-[var(--hk-ink)]">"{r.text}"</blockquote>
                  <figcaption className="mt-5 pt-5 border-t border-[var(--hk-border)] text-[13px] text-[var(--hk-ink-2)]">
                    <span className="font-medium text-[var(--hk-ink)]">{r.name}</span> · {r.city}
                  </figcaption>
                </figure>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Related Products */}
      <section className="relative py-20 bg-[var(--hk-warm-ivory)] overflow-hidden">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-6">
            <div>
              <div className="label-eyebrow">Continue Exploring</div>
              <h2 className="serif mt-2 text-[28px] sm:text-[36px] text-[var(--hk-ink)] leading-tight">
                Explore other <span className="italic-accent text-[var(--hk-orange-deep)]">HerbKranti</span> products.
              </h2>
            </div>
            <Link to="/products" className="hk-btn-ghost" data-testid="pdp-back-to-products">Back to Products</Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="pdp-related-grid">
            {related.map((p) => (
              <Link key={p.slug} to={`/products/${p.slug}`} className="hk-card overflow-hidden flex flex-col group" data-testid={`pdp-related-${p.slug}`}>
                <div className="aspect-square overflow-hidden bg-white">
                  <ProductImage product={p} imgClassName="group-hover:scale-105" />
                </div>
                <div className="p-5">
                  <div className="label-eyebrow !text-[10px] text-[var(--hk-orange-deep)]">{p.statusLabel}</div>
                  <h3 className="serif mt-2 text-[18px] leading-tight text-[var(--hk-ink)]">{p.name}</h3>
                  <p className="mt-2 text-[12.5px] text-[var(--hk-ink-2)] line-clamp-2">{p.shortLine}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default ProductDetailPage;
